import { z } from "zod";

const serverEnvSchema = z.object({
  SUPABASE_SERVICE_ROLE_KEY: z.string().min(1, "SUPABASE_SERVICE_ROLE_KEY is required"),
  OWNER_PIN_SECRET: z.string().min(4, "OWNER_PIN_SECRET must be at least 4 characters").optional(),
  REDIS_URL: z.string().optional(),
  REDIS_PASSWORD: z.string().optional(),
  REDIS_LOCAL_HOST: z.string().optional(),
  REDIS_LOCAL_PORT: z.string().optional(),
  REDIS_DB: z.string().optional(),
  TUNNEL_SERVICE_TOKEN_ID: z.string().optional(),
  TUNNEL_SERVICE_TOKEN_SECRET: z.string().optional(),
  REDIS_TUNNEL_HOST: z.string().optional(),
  SENTRY_DSN: z.string().optional(),
  GOOGLE_CLIENT_ID: z.string().optional(),
  GOOGLE_CLIENT_SECRET: z.string().optional(),
  RESEND_API_KEY: z.string().optional(),
  RESEND_VERIFIED_DOMAIN: z.string().optional(),
  OPENAI_API_KEY: z.string().min(1, "OPENAI_API_KEY is required for AI features"),
  OPENAI_MODEL: z.string().default("gpt-4o-mini"),
});

const clientEnvSchema = z.object({
  NEXT_PUBLIC_SUPABASE_URL: z.string().url("NEXT_PUBLIC_SUPABASE_URL must be a valid URL"),
  NEXT_PUBLIC_SUPABASE_ANON_KEY: z.string().min(1, "NEXT_PUBLIC_SUPABASE_ANON_KEY is required"),
  NEXT_PUBLIC_GRAPHQL_ENDPOINT: z.string().optional().default(""),
  NEXT_PUBLIC_SENTRY_DSN: z.string().optional(),
});

function validateServerEnv() {
  if (typeof window !== "undefined") {
    throw new Error("Server env should not be accessed on the client");
  }

  const parsed = serverEnvSchema.safeParse(process.env);

  if (!parsed.success) {
    return {
      SUPABASE_SERVICE_ROLE_KEY: process.env.SUPABASE_SERVICE_ROLE_KEY ?? "",
      OWNER_PIN_SECRET: process.env.OWNER_PIN_SECRET,
      REDIS_URL: process.env.REDIS_URL,
      REDIS_PASSWORD: process.env.REDIS_PASSWORD,
      REDIS_LOCAL_HOST: process.env.REDIS_LOCAL_HOST,
      REDIS_LOCAL_PORT: process.env.REDIS_LOCAL_PORT,
      REDIS_DB: process.env.REDIS_DB,
      TUNNEL_SERVICE_TOKEN_ID: process.env.TUNNEL_SERVICE_TOKEN_ID,
      TUNNEL_SERVICE_TOKEN_SECRET: process.env.TUNNEL_SERVICE_TOKEN_SECRET,
      REDIS_TUNNEL_HOST: process.env.REDIS_TUNNEL_HOST,
      SENTRY_DSN: process.env.SENTRY_DSN,
      GOOGLE_CLIENT_ID: process.env.GOOGLE_CLIENT_ID,
      GOOGLE_CLIENT_SECRET: process.env.GOOGLE_CLIENT_SECRET,
      OPENAI_API_KEY: process.env.OPENAI_API_KEY ?? "",
      OPENAI_MODEL: process.env.OPENAI_MODEL ?? "gpt-4o-mini",
    };
  }

  return parsed.data;
}

function validateClientEnv() {
  const env = {
    NEXT_PUBLIC_SUPABASE_URL: process.env.NEXT_PUBLIC_SUPABASE_URL,
    NEXT_PUBLIC_SUPABASE_ANON_KEY: process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY,
    NEXT_PUBLIC_GRAPHQL_ENDPOINT: process.env.NEXT_PUBLIC_GRAPHQL_ENDPOINT,
    NEXT_PUBLIC_SENTRY_DSN: process.env.NEXT_PUBLIC_SENTRY_DSN,
    NEXT_PUBLIC_APP_URL: process.env.NEXT_PUBLIC_APP_URL,
  };

  const parsed = clientEnvSchema.safeParse(env);

  if (!parsed.success) {
    return {
      NEXT_PUBLIC_SUPABASE_URL: process.env.NEXT_PUBLIC_SUPABASE_URL ?? "",
      NEXT_PUBLIC_SUPABASE_ANON_KEY: process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY ?? "",
      NEXT_PUBLIC_GRAPHQL_ENDPOINT: process.env.NEXT_PUBLIC_GRAPHQL_ENDPOINT ?? "",
      NEXT_PUBLIC_SENTRY_DSN: process.env.NEXT_PUBLIC_SENTRY_DSN,
    };
  }

  return parsed.data;
}

export const serverEnv = typeof window === "undefined" ? validateServerEnv() : null;
export const clientEnv = validateClientEnv();

export type ServerEnv = z.infer<typeof serverEnvSchema>;
export type ClientEnv = z.infer<typeof clientEnvSchema>;
